import React, { useEffect, useRef, useState } from 'react'
import { CButton, CCardBody } from '@coreui/react-pro'
import { Button, Input, InputRef, Space, Table } from 'antd'
import { useGiftsList } from './hooks'
import { ColumnsType } from 'antd/lib/table'
import { Gifts } from './types'
import { DataIndex } from './types'
import { SearchOutlined } from '@ant-design/icons'
import Highlighter from 'react-highlight-words'
import { FilterValue } from 'antd/es/table/interface'
import { path } from '../../../../libs'
import CIcon from '@coreui/icons-react'
import { cilZoom } from '@coreui/icons'
import { useUnit } from 'effector-react'
import { $countriesList } from '../../../../features/dictionaries/model'
import { transformCountriesToOptions } from '../../../../libs/formatCountries'
import { getMinVersions } from '../utils'

export const GiftsList = () => {
  const { gifts, totalCountOfGifts, loading, onChangeGiftsList } =
    useGiftsList()
  const countriesList = useUnit($countriesList)

  const [searchText, setSearchText] = useState<{
    [key: string]: string
  }>({})
  const [orderBy, setOrderBy] = useState('id')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({})

  const searchInput = useRef<InputRef>(null)

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    filters,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    filters?: Record<string, FilterValue | null>
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      if (currentOrderDirection == 'descend') {
        setOrderDirection('-')
      } else {
        setOrderDirection('')
      }
    }

    if (filters) {
      setFiltersState(filters)
    }
  }

  const handleReset = (clearFilters: () => void, dataIndex: DataIndex) => {
    clearFilters()
    setSearchText((prev) => {
      delete prev[dataIndex]
      return {
        ...prev,
      }
    })
  }

  useEffect(() => {
    onChangeGiftsList({
      limit: pageSize,
      page: page,
      orderBy: orderBy,
      orderDirection: orderDirection,
      searchText: searchText,
      filters: filtersState,
    })
  }, [pageSize, page, orderBy, orderDirection, searchText, filtersState])

  const getColumnSearchProps = (
    dataIndex: DataIndex,
  ): {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: {
      setSelectedKeys: any
      selectedKeys: any
      confirm: any
      clearFilters?: any
      close: any
    }) => JSX.Element
    filterIcon: (filtered: boolean) => JSX.Element
    onFilter: (value: any, record: any) => boolean
    render: (text: any) => any
    onFilterDropdownOpenChange: (visible: any) => void
  } => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            setSearchText((prev) => ({ ...prev, [dataIndex]: selectedKeys[0] }))
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              setSearchText((prev) => ({
                ...prev,
                [dataIndex]: selectedKeys[0],
              }))
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close()
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: (text) =>
      searchText[dataIndex] ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText[dataIndex]]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  const columns: ColumnsType<Gifts> = [
    {
      title: 'ID',
      dataIndex: 'id',
      ...getColumnSearchProps('id'),
      sorter: (a, b) => a.id - b.id,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Name',
      dataIndex: 'title',
      key: 'title',
      ...getColumnSearchProps('title'),
      sorter: true,
    },
    {
      title: 'Price',
      dataIndex: 'price',
      ...getColumnSearchProps('price'),
      sorter: true,
    },
    {
      title: 'Cost price',
      dataIndex: 'costPrice',
      ...getColumnSearchProps('costPrice'),
      sorter: true,
    },
    {
      title: 'Category',
      dataIndex: ['giftCategory', 'name'],
      key: 'giftCategory.name',
      sorter: true,
      defaultSortOrder: 'descend',
      render: (categoryId, record) => record.giftCategory?.name,
    },
    {
      title: 'Version min',
      key: 'displayRequirements',
      render: (_, record) => {
        const { versionMinIos, versionMinAndroid } = getMinVersions(
          record.displayRequirements,
        )

        return (
          <>
            {versionMinIos && <div>iOS: {versionMinIos}</div>}
            {versionMinAndroid && <div>Android: {versionMinAndroid}</div>}
          </>
        )
      },
    },
    {
      title: 'Country',
      dataIndex: 'availableCountries',
      filters: transformCountriesToOptions(countriesList),
      filterSearch: true,
      onFilter: (value: any, record) =>
        record.availableCountries?.includes(value),
      render: (item: string[]) => item?.map((country) => ' ' + country),
    },
    {
      title: 'Sort',
      dataIndex: 'sorting',
      key: 'sorting',
      sorter: (a, b) => a.sorting - b.sorting,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Actions',
      fixed: 'right',
      width: 100,
      render: (item) => {
        return (
          <>
            <CButton
              color="secondary"
              className="me-2"
              title="View"
              href={path.giftShop.gifts.details(item.id)}
            >
              <CIcon icon={cilZoom} />
            </CButton>
          </>
        )
      },
    },
  ]

  return (
    <CCardBody>
      <CButton
        color="primary"
        className="mb-2"
        href={path.giftShop.gifts.create()}
      >
        Create gift
      </CButton>
      <Table
        dataSource={gifts}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey="giftId"
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfGifts,
        }}
        onChange={(pagination, filters, sorter, extra) =>
          handleTableChange({
            //@ts-ignore
            currentPage: pagination.current, //@ts-ignore
            currentPageSize: pagination.pageSize, //@ts-ignore
            currentOrderDirection: sorter.order, //@ts-ignore
            currentOrderBy: sorter.field,
            filters,
          })
        }
      ></Table>
    </CCardBody>
  )
}
